import { getRange } from '../../helpers/axisDef';
import { renderXAxisLabels, renderYAxisLabels } from '../../zoompan-canvas/renderAxes';
import {
  getBoundData,
  getDendrogramBinding,
  isDendrogramBinding,
  TpChartConfigParams,
  TpChartDataBinding,
} from '../chartDataBindings';
import {
  createLogicalRange as createLogicalRange,
  RenderMinDiffAbs,
  RenderMaxDiffAbs,
  createRenderRange as createRenderRange,
  TpAreaRenderContext,
  TpChartAreaDefinition,
} from '../../zoompan-canvas/zoomPanCanvasHelpers';
import { TpDataContext, TpDataTable } from '../../../../data/interfaces';
import { getDendrogram } from '../../../../data/analyticsDataSlice';
import { getNumericalColorSet } from '../../helpers/colors';
import { LEGEND_ITEM_TYPES, TpLegendItem, renderLegendItems, renderYLabel } from '../../helpers/renderLegend';
import { TpChartManifest } from '../interfaces';
import {
  createColorMappingInfo,
  createNumericalScaleStops,
  getMappedColorLegendInfo,
} from '../../helpers/colorMappingInfo';
import {
  chartDims,
  convertColumnDataListToDisplayString,
  createSortIndexes,
  generateChartTitle,
} from '../../helpers/misc';
import { currentTheme, getThemedGrayFraction } from '../../../themes/themes';
import { TpDashboardGlobalChartConfig } from '../../dashboard/interfaces';
import { getLabelSpaceSize } from '../../helpers/configParams';

export function createDefinition(
  chartTypeManifest: TpChartManifest,
  dataContext: TpDataContext,
  dataBinding: TpChartDataBinding,
  config: TpChartConfigParams,
  widgetGlobalConfig: TpDashboardGlobalChartConfig,
) {
  const boundData = getBoundData(chartTypeManifest, dataContext, dataBinding);

  const dataTable: TpDataTable = boundData.dataTable;
  const columnCanonicalTypeInfo = boundData.matrixColumnInfo!.columnCanonicalTypeInfo;
  const matrixColumns = boundData.matrixColumnInfo!.matrixColumns;
  const dataTableColumns = boundData.matrixColumnInfo!.dataTableColumns;
  const hoverLabelColumnInfo = boundData.bindings.hoverLabelColumn.columnInfo;
  const dataHoverLabelRow = boundData.bindings.hoverLabelRow.data;
  const hoverLabelRowInfo = boundData.bindings.hoverLabelRow.columnInfo;
  const dataHoverLabelcolumn = boundData.bindings.hoverLabelColumn.data;
  const matrixColumnsData = boundData.matrixColumnInfo!.matrixColumnsData;
  const rowRestrictionFilter = boundData.restrictionFilter;
  const getItemIndexById = boundData.getItemIndexById;

  const dataRowItemIds = boundData.bindings.itemId.data;

  const dataRowLabels = convertColumnDataListToDisplayString(
    boundData.bindings.rowLabelId.columnInfo,
    boundData.bindings.rowLabelId.data,
  );
  const dataColumnLabels = convertColumnDataListToDisplayString(
    boundData.bindings.columnLabelId.columnInfo,
    boundData.bindings.columnLabelId.data,
  );
  //const dataRowItemId = boundData.bindings.itemId.data;

  const rowCount = dataRowItemIds.length;
  const colCount = matrixColumns.length;

  const [rowSortIndex, rowSortInvIndex] = createSortIndexes(
    rowCount,
    true,
    boundData.bindings.sortRowBy?.columnInfo,
    boundData.bindings.sortRowBy?.data,
  );
  const [colSortIndex, colSortInvIndex] = createSortIndexes(
    colCount,
    false,
    boundData.bindings.sortColumnBy?.columnInfo,
    boundData.bindings.sortColumnBy?.data,
  );

  const hasRowsDendrogram =
    // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
    isDendrogramBinding(dataBinding.aspectBindings.sortRowBy?.columnId!) &&
    !boundData.canonicalTypeInfo.restrictedItemsMap;
  // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
  const hasColumnsDendrogram = isDendrogramBinding(dataBinding.aspectBindings.sortColumnBy?.columnId!);

  const globalColorMappingConfig = widgetGlobalConfig.colorMapping;
  const rowColorMappingInfo = createColorMappingInfo(config, boundData, 'rowColor', 0.45, globalColorMappingConfig);

  let minVal = 1e99;
  let maxVal = -1e99;
  for (const column of matrixColumns) {
    const columnData = dataTable.columnData[column.id];
    const [minValCol, maxValCol] = getRange(columnData, 0);
    if (minValCol < minVal) minVal = minValCol;
    if (maxValCol > maxVal) maxVal = maxValCol;
  }

  const colorValueStrings = getNumericalColorSet(config.colorSchemeMatrixVals).colors.map((color) =>
    color.toCanvasString(),
  );

  function getColorForMissingValues() {
    if (config.colorMissingValues === 'empty') return 'transparent';

    // This is the backwards compatible default.
    if (config.colorMissingValues === 'minValue') {
      return colorValueStrings[0];
    }

    // Default to neutral color if the config value is invalid for some reason.
    return getThemedGrayFraction(0.85).toCanvasString();
  }

  function getCellColor(cellValue: number, gammaFactor: number) {
    if (isNaN(cellValue)) return getColorForMissingValues();
    const colorFrac = ((cellValue - minVal) / (maxVal - minVal)) ** gammaFactor;
    return colorValueStrings[
      Math.round(Math.max(0, Math.min(colorValueStrings.length - 1, colorFrac * colorValueStrings.length)))
    ];
  }

  function renderColumnLabels(renderContext: TpAreaRenderContext) {
    renderXAxisLabels(renderContext, dataColumnLabels, colSortIndex);
  }

  function renderRowLabels(renderContext: TpAreaRenderContext) {
    renderYAxisLabels(
      renderContext,
      boundData.canonicalTypeInfo.selectedItemsMap,
      dataRowLabels,
      dataRowItemIds,
      rowColorMappingInfo,
      rowSortIndex,
    );
  }

  function renderRowDendrogram(renderContext: TpAreaRenderContext) {
    const { mapx, mapy, pixelConv, mapinvx, mapinvy, areaYMin, areaYMax, pixelInvConv } = renderContext;
    const ctx = renderContext.canvasContext;
    // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
    const sortColumnId = dataBinding.aspectBindings.sortRowBy?.columnId!;
    const dendrogram = getDendrogram(dataContext, getDendrogramBinding(sortColumnId));
    const fc = 0.95 / dendrogram.tree.distance;
    ctx.strokeStyle = currentTheme.colors.text.toCanvasString();
    function renderTree(node: any) {
      if (!node.itemId) {
        renderTree(node.leaf1);
        renderTree(node.leaf2);
        ctx.beginPath();
        ctx.moveTo(mapx(1 - node.leaf1.distance * fc) - pixelConv(2), mapy(1 - node.leaf1.index / rowCount));
        ctx.lineTo(mapx(1 - node.distance * fc) - pixelConv(2), mapy(1 - node.leaf1.index / rowCount));
        ctx.lineTo(mapx(1 - node.distance * fc) - pixelConv(2), mapy(1 - node.leaf2.index / rowCount));
        ctx.lineTo(mapx(1 - node.leaf2.distance * fc) - pixelConv(2), mapy(1 - node.leaf2.index / rowCount));
        ctx.stroke();
      }
    }
    renderTree(dendrogram.tree);
  }

  function renderColumnDendrogram(renderContext: TpAreaRenderContext) {
    const { mapx, mapy, pixelConv, mapinvx, mapinvy, areaYMin, areaYMax, pixelInvConv } = renderContext;
    const ctx = renderContext.canvasContext;
    // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
    const sortColumnsFieldId = dataBinding.aspectBindings.sortColumnBy?.columnId!;
    const dendrogram = getDendrogram(dataContext, getDendrogramBinding(sortColumnsFieldId));
    const fc = 0.95 / dendrogram.tree.distance;
    ctx.strokeStyle = currentTheme.colors.text.toCanvasString();
    function renderTree(node: any) {
      if (!node.itemId) {
        renderTree(node.leaf1);
        renderTree(node.leaf2);
        ctx.beginPath();
        ctx.moveTo(mapx(node.leaf1.index / colCount), mapy(node.leaf1.distance * fc) - pixelConv(2));
        ctx.lineTo(mapx(node.leaf1.index / colCount), mapy(node.distance * fc) - pixelConv(2));
        ctx.lineTo(mapx(node.leaf2.index / colCount), mapy(node.distance * fc) - pixelConv(2));
        ctx.lineTo(mapx(node.leaf2.index / colCount), mapy(node.leaf2.distance * fc) - pixelConv(2));
        ctx.stroke();
      }
    }
    renderTree(dendrogram.tree);
  }

  function renderMatrix(renderContext: TpAreaRenderContext) {
    const { mapx, mapy, pixelConv, mapinvx, mapinvy, areaYMin, areaYMax } = renderContext;
    const ctx = renderContext.canvasContext;

    ctx.textAlign = 'left';
    ctx.textBaseline = 'top';
    renderContext.setFont(8);

    const startRownr = Math.max(0, Math.min(rowCount - 1, Math.floor(mapinvy(areaYMin) * rowCount)));
    const endRowNr = Math.max(0, Math.min(rowCount - 1, Math.ceil(mapinvy(areaYMax) * rowCount)));

    const rowBinSize = Math.max(1, Math.round((endRowNr - startRownr) / 400));
    const rowBins = [];
    for (let colNr = Math.floor(startRownr / rowBinSize) * rowBinSize; colNr <= endRowNr; ) {
      const rowBin = [];
      for (let idx = 0; idx < rowBinSize; idx++) {
        rowBin.push(colNr);
        colNr++;
      }
      rowBins.push(rowBin);
    }

    const gammaFactor = Math.exp(config.gamma / 2) / Math.exp(5 / 2);

    for (let colNr = 0; colNr < matrixColumns.length; colNr++) {
      const column = matrixColumns[colSortIndex[colNr]];
      const columnData = boundData.matrixColumnInfo!.matrixColumnsData[column.id];

      for (const rowBin of rowBins) {
        let aggrValue = -1e99;
        const hasValue = rowBin.some((row) => !isNaN(columnData[rowSortIndex[row]]));
        // Bin aggrValue is the maximum value of the cells in the bin
        for (const row of rowBin) {
          const rowValue = columnData[rowSortIndex[row]];
          if (rowValue > aggrValue) aggrValue = rowValue; // @todo: we need other aggregation rules as well!
        }
        ctx.fillStyle = getCellColor(hasValue ? aggrValue : NaN, gammaFactor);
        ctx.fillRect(
          mapx(colNr / colCount),
          mapy((rowBin[0] + 1) / rowCount), // need offset one because we meaure from bottom to top!
          mapx((colNr + 1) / colCount) - mapx(colNr / colCount),
          mapy(rowBin[0] / rowCount) - mapy((rowBin[0] + rowBinSize) / rowCount),
        );
      }

      // for (let rowNr = 0; rowNr < rowCount; rowNr++) {
      //     const cellValue = columnData[rowSortIndex[rowNr]];
      //     const colorFrac = (cellValue - minVal) / (maxVal - minVal);
      //     const colorStr = colorValueStrings[Math.round(Math.max(0, Math.min(colorValueStrings.length - 1, colorFrac * colorValueStrings.length)))];
      //     ctx.fillStyle = colorStr;
      //     ctx.fillRect(
      //         mapx(colNr / colCount),
      //         mapy((rowNr + 1) / rowCount), // need offset one because we mesaure from bottom to top!
      //         mapx((colNr + 1) / colCount) - mapx(colNr / colCount),
      //         Math.abs(mapy(rowNr / rowCount) - mapy((rowNr + 1) / rowCount))
      //     );
      // }
    }
  }

  function renderLegend(renderContext: TpAreaRenderContext) {
    const legendItems: TpLegendItem[] = [];
    legendItems.push({
      type: LEGEND_ITEM_TYPES.NumericalColors,
      colorColumnInfo: null,
      colorValueStrings: colorValueStrings,
      minValColor: minVal,
      maxValColor: maxVal,
      gammaFactor: Math.exp(config.gamma / 2) / Math.exp(5 / 2),
      numericalLegendItems: createNumericalScaleStops(minVal, maxVal),
    });

    if (rowColorMappingInfo.hasColors) legendItems.push(getMappedColorLegendInfo(rowColorMappingInfo));
    renderLegendItems(renderContext, legendItems);
  }

  const legendBottomSizeHeight = chartDims.legendHeight;

  const titleSize = chartDims.headerHeight;
  const rowLabelSize = getLabelSpaceSize(config.labelSpaceRows);
  const columnLabelSize = getLabelSpaceSize(config.labelSpaceColumns);

  const title = generateChartTitle(dataContext, dataBinding, config, '');

  const rowDendrogramSize = hasRowsDendrogram ? 80 : 0;
  const columnDendrogramSize = hasColumnsDendrogram ? 40 : 0;

  const padding = chartDims.padding;

  const zpLogicalRangeX = createLogicalRange(0, 1);
  const zpLogicalRangeY = createLogicalRange(0, 1);
  const zpRenderRangeX = createRenderRange(
    RenderMinDiffAbs(padding + rowDendrogramSize),
    RenderMaxDiffAbs(padding + rowLabelSize),
  );
  const zpRenderRangeY = createRenderRange(
    RenderMinDiffAbs(padding + titleSize + columnDendrogramSize),
    RenderMaxDiffAbs(padding + columnLabelSize + legendBottomSizeHeight),
  );

  const areaMatrix: TpChartAreaDefinition = {
    id: 'matrix',
    logicalRangeX: zpLogicalRangeX,
    logicalRangeY: zpLogicalRangeY,
    renderRangeX: zpRenderRangeX,
    renderRangeY: zpRenderRangeY,
    zoomPanX: true,
    zoomPanY: true,
    // canAreaSelect: true,
    render: renderMatrix,
  };

  const chartAreas: TpChartAreaDefinition[] = [
    areaMatrix,
    {
      id: 'title',
      renderRangeX: createRenderRange(RenderMinDiffAbs(padding + 0), RenderMaxDiffAbs(padding + 0)),
      renderRangeY: createRenderRange(
        RenderMinDiffAbs(padding + 0),
        RenderMinDiffAbs(padding + chartDims.headerHeight),
      ),
      render: (renderContext: TpAreaRenderContext) => renderYLabel(renderContext, '', title),
    },

    {
      id: 'columnLabels',
      logicalRangeX: zpLogicalRangeX,
      logicalRangeY: createLogicalRange(0, 1),
      renderRangeX: zpRenderRangeX,
      renderRangeY: createRenderRange(
        RenderMaxDiffAbs(padding + columnLabelSize + legendBottomSizeHeight),
        RenderMaxDiffAbs(padding + legendBottomSizeHeight),
      ),
      zoomPanX: true,
      render: renderColumnLabels,
    },
    {
      id: 'rowLabels',
      logicalRangeX: createLogicalRange(0, 1),
      logicalRangeY: zpLogicalRangeY,
      renderRangeX: createRenderRange(RenderMaxDiffAbs(padding + rowLabelSize), RenderMaxDiffAbs(padding)),
      renderRangeY: zpRenderRangeY,
      zoomPanY: true,
      render: renderRowLabels,
    },
  ];

  if (hasRowsDendrogram)
    chartAreas.push({
      id: 'rowDendrogram',
      logicalRangeX: createLogicalRange(0, 1),
      logicalRangeY: zpLogicalRangeY,
      renderRangeX: createRenderRange(RenderMinDiffAbs(padding), RenderMinDiffAbs(padding + rowDendrogramSize)),
      renderRangeY: zpRenderRangeY,
      zoomPanY: true,
      render: renderRowDendrogram,
    });

  if (hasColumnsDendrogram)
    chartAreas.push({
      id: 'columnDendrogram',
      logicalRangeX: zpLogicalRangeX,
      logicalRangeY: createLogicalRange(0, 1),
      renderRangeX: zpRenderRangeX,
      renderRangeY: createRenderRange(
        RenderMinDiffAbs(padding + titleSize),
        RenderMinDiffAbs(padding + titleSize + columnDendrogramSize),
      ),
      zoomPanX: true,
      render: renderColumnDendrogram,
    });

  chartAreas.push({
    id: 'legend',
    renderRangeX: createRenderRange(RenderMinDiffAbs(padding), RenderMaxDiffAbs(padding + 0)),
    renderRangeY: createRenderRange(RenderMaxDiffAbs(padding + legendBottomSizeHeight), RenderMaxDiffAbs(padding)),
    render: renderLegend,
  });

  const definition = {
    zpLogicalRangeX,
    zpLogicalRangeY,
    zpRenderRangeX,
    zpRenderRangeY,
    areas: chartAreas,
    columnCanonicalTypeInfo,
    matrixColumns,
    dataTableColumns,
  };

  return {
    definition,
    areaMatrix,
    dataTable,
    dataRowItemIds,
    dataRowLabels,
    dataColumnLabels,
    hoverLabelRowInfo,
    dataHoverLabelRow,
    hoverLabelColumnInfo,
    dataHoverLabelcolumn,
    columnCanonicalTypeInfo,
    matrixColumns,
    rowSortIndex,
    rowSortInvIndex,
    colSortIndex,
    colSortInvIndex,
    getItemIndexById,
    matrixColumnsData,
    title,
  };
}
